import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import { Input } from '@/app/components/ui/input';
import { Label } from '@/app/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/app/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/app/components/ui/dialog';
import { toast } from '@/app/components/ui/simple-toaster';
import { useCreatePerson } from '@/hooks/use-persons';
import { handleApiError } from '@/lib/api-client';
import { useOrganizationSettings } from '@/hooks/use-settings';
import type { Person } from '@/lib/types';

interface AddPersonDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated?: (person: Person) => void;
}

interface PersonFormData {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  address: string;
  city: string;
  state_province: string;
  country: string;
  postal_code: string;
  adp_file_number: string;
  xero_employee_number: string;
  rope_access_level: string;
  rope_access_body: string;
}

const initialForm: PersonFormData = {
  first_name: '',
  last_name: '',
  email: '',
  phone: '',
  address: '',
  city: '',
  state_province: '',
  country: '',
  postal_code: '',
  adp_file_number: '',
  xero_employee_number: '',
  rope_access_level: '',
  rope_access_body: '',
};

export function AddPersonDialog({ open, onOpenChange, onCreated }: AddPersonDialogProps) {
  const [form, setForm] = useState<PersonFormData>(initialForm);
  const [error, setError] = useState<string | null>(null);
  const createMutation = useCreatePerson();
  const { data: settings } = useOrganizationSettings();
  const crewEnabled = settings?.modules.crew ?? false;

  const updateField = (field: keyof PersonFormData, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleClose = (val: boolean) => {
    if (!val) {
      setForm(initialForm);
      setError(null);
    }
    onOpenChange(val);
  };

  const handleSubmit = async () => {
    setError(null);
    if (!form.first_name.trim() || !form.last_name.trim()) {
      setError('First name and last name are required.');
      return;
    }

    try {
      const payload = {
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        email: form.email.trim() || undefined,
        phone: form.phone.trim() || undefined,
        address: form.address.trim() || undefined,
        city: form.city.trim() || undefined,
        state_province: form.state_province.trim() || undefined,
        country: form.country.trim() || undefined,
        postal_code: form.postal_code.trim() || undefined,
        adp_file_number: form.adp_file_number.trim() || undefined,
        xero_employee_number: form.xero_employee_number.trim() || undefined,
        ...(!crewEnabled
          ? {
              rope_access_level: form.rope_access_level as '' | 'l1' | 'l2' | 'l3',
              rope_access_body: form.rope_access_body as '' | 'irata' | 'sprat' | 'other',
            }
          : {}),
      };
      const person = await createMutation.mutateAsync(payload);
      toast.success(`${person.full_name} created`);
      handleClose(false);
      onCreated?.(person);
    } catch (err) {
      const apiError = handleApiError(err);
      setError(apiError.message);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Add Person</DialogTitle>
          <DialogDescription>Add a new person to your organization.</DialogDescription>
        </DialogHeader>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-lg text-sm">
            {error}
          </div>
        )}

        <div className="space-y-4 py-2">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="person-first-name">First Name *</Label>
              <Input
                id="person-first-name"
                value={form.first_name}
                onChange={(e) => updateField('first_name', e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="person-last-name">Last Name *</Label>
              <Input
                id="person-last-name"
                value={form.last_name}
                onChange={(e) => updateField('last_name', e.target.value)}
              />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="person-email">Email</Label>
              <Input
                id="person-email"
                type="email"
                value={form.email}
                onChange={(e) => updateField('email', e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="person-phone">Phone</Label>
              <Input
                id="person-phone"
                value={form.phone}
                onChange={(e) => updateField('phone', e.target.value)}
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="person-address">Address</Label>
            <Input
              id="person-address"
              value={form.address}
              onChange={(e) => updateField('address', e.target.value)}
            />
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label htmlFor="person-city">City</Label>
              <Input
                id="person-city"
                value={form.city}
                onChange={(e) => updateField('city', e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="person-state">State / Province</Label>
              <Input
                id="person-state"
                value={form.state_province}
                onChange={(e) => updateField('state_province', e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="person-country">Country</Label>
              <Input
                id="person-country"
                value={form.country}
                onChange={(e) => updateField('country', e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="person-postal">Postal Code</Label>
              <Input
                id="person-postal"
                value={form.postal_code}
                onChange={(e) => updateField('postal_code', e.target.value)}
              />
            </div>
          </div>

          <div className="pt-2 border-t space-y-3">
            <div>
              <h4 className="text-sm font-medium text-gray-900">Rope Access</h4>
              {crewEnabled && (
                <p className="text-xs text-gray-500 mt-1">
                  Managed from the person&apos;s current H3Crew certification.
                </p>
              )}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Level</Label>
                <Select
                  value={form.rope_access_level || 'none'}
                  onValueChange={(value) =>
                    updateField('rope_access_level', value === 'none' ? '' : value)
                  }
                  disabled={crewEnabled}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Not set</SelectItem>
                    <SelectItem value="l1">L1</SelectItem>
                    <SelectItem value="l2">L2</SelectItem>
                    <SelectItem value="l3">L3</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Body</Label>
                <Select
                  value={form.rope_access_body || 'none'}
                  onValueChange={(value) =>
                    updateField('rope_access_body', value === 'none' ? '' : value)
                  }
                  disabled={crewEnabled}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Not set</SelectItem>
                    <SelectItem value="irata">IRATA</SelectItem>
                    <SelectItem value="sprat">SPRAT</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <div className="pt-2 border-t">
            <h4 className="text-sm font-medium text-gray-900 mb-3">Payroll Integration</h4>
            <p className="text-xs text-gray-500 mb-3">
              Optional identifiers used only when exporting payroll to ADP or Xero.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="person-adp-file-number">ADP File Number</Label>
                <Input
                  id="person-adp-file-number"
                  value={form.adp_file_number}
                  onChange={(e) => updateField('adp_file_number', e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="person-xero-employee-number">Xero Employee Number</Label>
                <Input
                  id="person-xero-employee-number"
                  value={form.xero_employee_number}
                  onChange={(e) => updateField('xero_employee_number', e.target.value)}
                />
              </div>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleClose(false)}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={createMutation.isPending}>
            {createMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Add Person
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
